/**
 * Risk matrix editor — parity with Angular `ProductEditComponent` risk matrix block.
 */
import type { ForwardedRef } from 'react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Loader2, Lock, Unlock, Plus, Trash2, ShieldCheck } from 'lucide-react'
import { SectionCard } from '@/components/ui/SectionCard'
import { showSuccess } from '@/lib/utils/notifications'
import type {
  ProductModelOption,
  ProductRiskMatrixTabHandle,
  ProductRiskMatrixTabProps,
  RiskLabel,
  ScoreRange,
} from '../types'
import { fetchRiskMatrix, saveRiskMatrix } from '../services/productApi'
import {
  DEFAULT_RISK_COLORS,
  buildMatrixPayload,
  findRiskLabel,
  formatRiskLabelText,
  getRangeKey,
  getRangeLabel,
  getRiskCellTextColor,
  hasDuplicateRiskLevelNumbers,
  hexToLightBg,
  isRiskLevelNumberTaken,
  mapRiskLabel,
  normalizeRiskLabelText,
  scoreRangesOverlap,
  validateRiskLabels,
  validateScoreRanges,
} from '../utils/productRiskMatrixModel'
import { RiskMatrixCellSelect } from './RiskMatrixCellSelect'

export const ProductRiskMatrixTab = forwardRef(
  function ProductRiskMatrixTab(
    { productId, onError, onUiState }: ProductRiskMatrixTabProps,
    ref: ForwardedRef<ProductRiskMatrixTabHandle>,
  ) {
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [locked, setLocked] = useState(true)
    const [dirty, setDirty] = useState(false)
    const dirtyRef = useRef(dirty)
    const lockedRef = useRef(locked)
    dirtyRef.current = dirty
    lockedRef.current = locked

    useEffect(() => {
      onUiState?.({ dirty, locked })
    }, [dirty, locked, onUiState])
    const [subTab, setSubTab] = useState<'single' | 'multiple'>('single')

    const [equifaxSingle, setEquifaxSingle] = useState<ScoreRange[]>([])
    const [equifaxMultiple, setEquifaxMultiple] = useState<ScoreRange[]>([])
    const [ninjaSingle, setNinjaSingle] = useState<ScoreRange[]>([])
    const [ninjaMultiple, setNinjaMultiple] = useState<ScoreRange[]>([])
    const [labelsSingle, setLabelsSingle] = useState<RiskLabel[]>([])
    const [labelsMultiple, setLabelsMultiple] = useState<RiskLabel[]>([])
    const [singleMatrix, setSingleMatrix] = useState<Record<string, number>>({})
    const [multipleMatrix, setMultipleMatrix] = useState<Record<string, number>>({})
    /**
     * Bumped on every (re)load so the blur-committed numeric/percentage inputs — which are uncontrolled
     * (defaultValue) to mirror Angular's `[value]` + `(change)` commit-on-blur — resync their DOM value
     * after a load/cancel/reload.
     */
    const [formVersion, setFormVersion] = useState(0)
    const [productModels, setProductModels] = useState<ProductModelOption[]>([])
    const [selectedProductModelIds, setSelectedProductModelIds] = useState<{
      single: number | null
      multiple: number | null
    }>({ single: null, multiple: null })

    const tmpKeyRef = useRef(-1)

    const stateRef = useRef({
      equifaxSingle,
      equifaxMultiple,
      ninjaSingle,
      ninjaMultiple,
      labelsSingle,
      labelsMultiple,
      singleMatrix,
      multipleMatrix,
      subTab,
      selectedProductModelIds,
    })
    stateRef.current = {
      equifaxSingle,
      equifaxMultiple,
      ninjaSingle,
      ninjaMultiple,
      labelsSingle,
      labelsMultiple,
      singleMatrix,
      multipleMatrix,
      subTab,
      selectedProductModelIds,
    }

    const ensureSelectedProductModelExists = (
      applicantType: 'single' | 'multiple',
      models: ProductModelOption[],
      ids: { single: number | null; multiple: number | null },
    ) => {
      const selectedId = ids[applicantType]
      const exists = models.some((m) => m.pm_id === selectedId)
      if (!exists) return { ...ids, [applicantType]: null }
      return ids
    }

    const selectedProductModelId = selectedProductModelIds[subTab]

    const assignTmp = (r: ScoreRange) => {
      if (r.id != null || r.tmp_key != null) return
      r.tmp_key = tmpKeyRef.current--
    }

    const currentEq = subTab === 'single' ? equifaxSingle : equifaxMultiple
    const currentNj = subTab === 'single' ? ninjaSingle : ninjaMultiple
    const currentLabels = subTab === 'single' ? labelsSingle : labelsMultiple
    const currentMatrix = subTab === 'single' ? singleMatrix : multipleMatrix
    const setCurrentMatrix = subTab === 'single' ? setSingleMatrix : setMultipleMatrix

    const setCurrentEq = subTab === 'single' ? setEquifaxSingle : setEquifaxMultiple
    const setCurrentNj = subTab === 'single' ? setNinjaSingle : setNinjaMultiple
    const setCurrentLabels = subTab === 'single' ? setLabelsSingle : setLabelsMultiple

    const buildMatrixFromResponse = useCallback(
      (values: { ninja_range_id: number; equifax_range_id: number; numeric_value: number }[], type: 'single' | 'multiple') => {
        const next: Record<string, number> = {}
        values.forEach((v) => {
          next[`${v.ninja_range_id}_${v.equifax_range_id}`] = v.numeric_value
        })
        if (type === 'single') setSingleMatrix(next)
        else setMultipleMatrix(next)
      },
      [],
    )

    const load = useCallback(async () => {
      if (!productId) return
      setLoading(true)
      try {
        const response = await fetchRiskMatrix(productId)
        setEquifaxSingle((response.equifax_ranges_single || []).map((r) => ({ ...r })))
        setEquifaxMultiple((response.equifax_ranges_multiple || []).map((r) => ({ ...r })))
        setNinjaSingle((response.ninja_ranges_single || []).map((r) => ({ ...r })))
        setNinjaMultiple((response.ninja_ranges_multiple || []).map((r) => ({ ...r })))

        const singleRaw = response.risk_labels_single || []
        const multipleRaw = response.risk_labels_multiple || []
        let ls = singleRaw.map((lbl, idx) => mapRiskLabel(lbl, idx, 'single'))
        let lm = multipleRaw.map((lbl, idx) => mapRiskLabel(lbl, idx, 'multiple'))
        if (ls.length === 0 && lm.length === 0 && response.risk_labels?.length) {
          const legacy = response.risk_labels
          ls = legacy
            .filter((l) => (l.applicant_type || 'single') === 'single')
            .map((lbl, idx) => mapRiskLabel(lbl, idx, 'single'))
          lm = legacy
            .filter((l) => (l.applicant_type || '') === 'multiple')
            .map((lbl, idx) => mapRiskLabel(lbl, idx, 'multiple'))
        }
        setLabelsSingle(ls)
        setLabelsMultiple(lm)
        const rv = response.risk_values || {}
        buildMatrixFromResponse(rv.single || [], 'single')
        buildMatrixFromResponse(rv.multiple || [], 'multiple')
        const models = (response.product_models || []).map((model) => ({
          pm_id: Number(model.pm_id),
          model_name: model.model_name,
          pkl_name: model.pkl_name,
        }))
        setProductModels(models)
        let modelIds = {
          single: response.product_model_single != null ? Number(response.product_model_single) : null,
          multiple: response.product_model_multiple != null ? Number(response.product_model_multiple) : null,
        }
        modelIds = ensureSelectedProductModelExists('single', models, modelIds)
        modelIds = ensureSelectedProductModelExists('multiple', models, modelIds)
        setSelectedProductModelIds(modelIds)
        setDirty(false)
        setFormVersion((v) => v + 1)
      } catch (e) {
        onError((e as { message?: string }).message || 'Failed to load risk matrix')
      } finally {
        setLoading(false)
      }
    }, [productId, onError, buildMatrixFromResponse])

    useEffect(() => {
      void load()
    }, [load])

    /**
     * Angular `getMatrixValue` self-heals: when a stored cell value no longer maps to any current
     * risk label, it writes the first-level fallback back into the matrix so the healed value is
     * persisted on save. React can't mutate state during render, so we heal the active sub-tab's
     * matrix here (only present-but-orphaned values are rewritten — undefined cells stay untouched).
     */
    useEffect(() => {
      const firstLevel = currentLabels.length > 0 ? currentLabels[0].numeric_value : 1
      let changed = false
      const next = { ...currentMatrix }
      currentNj.forEach((nj, ni) => {
        currentEq.forEach((eq, ei) => {
          const key = `${getRangeKey(nj, ni)}_${getRangeKey(eq, ei)}`
          const val = next[key]
          if (val !== null && val !== undefined && !findRiskLabel(currentLabels, val)) {
            next[key] = firstLevel
            changed = true
          }
        })
      })
      if (changed) setCurrentMatrix(next)
      // eslint-disable-next-line react-hooks/exhaustive-deps -- setCurrentMatrix/current* derive from subTab
    }, [subTab, currentLabels, currentEq, currentNj, currentMatrix])

    const getMatrixValue = (ninjaId: number, equifaxId: number): number => {
      const key = `${ninjaId}_${equifaxId}`
      const val = currentMatrix[key]
      const firstLevel = currentLabels.length > 0 ? currentLabels[0].numeric_value : 1
      if (val !== null && val !== undefined && findRiskLabel(currentLabels, val)) return val
      return firstLevel
    }

    const setMatrixValue = (ninjaId: number, equifaxId: number, value: number) => {
      const key = `${ninjaId}_${equifaxId}`
      setCurrentMatrix({ ...currentMatrix, [key]: value })
      setDirty(true)
    }

    const saveImpl = async (): Promise<boolean> => {
      if (lockedRef.current) {
        onError('Risk Matrix is locked. Click Unlock to edit and save.')
        return false
      }
      const s = stateRef.current
      const singleLabelErr = validateRiskLabels(s.labelsSingle, 'single')
      const multipleLabelErr = validateRiskLabels(s.labelsMultiple, 'multiple')
      const le = singleLabelErr || multipleLabelErr
      if (le) {
        // Angular switches to the sub-tab containing the invalid label before showing the error.
        setSubTab(singleLabelErr ? 'single' : 'multiple')
        onError(le)
        return false
      }
      if (hasDuplicateRiskLevelNumbers(s.labelsSingle) || hasDuplicateRiskLevelNumbers(s.labelsMultiple)) {
        onError('Risk level numbers must be unique. Please fix duplicates before saving.')
        return false
      }
      const eqSingleErr = validateScoreRanges(s.equifaxSingle, 'Eq', 'single')
      const eqMultiErr = validateScoreRanges(s.equifaxMultiple, 'Eq', 'multiple')
      const njSingleErr = validateScoreRanges(s.ninjaSingle, 'Ninja', 'single')
      const njMultiErr = validateScoreRanges(s.ninjaMultiple, 'Ninja', 'multiple')
      const rangeErr = eqSingleErr || eqMultiErr || njSingleErr || njMultiErr
      if (rangeErr) {
        onError(rangeErr)
        return false
      }

      const riskLabelsPayload = [
        ...s.labelsSingle.map((l) => ({ ...l, label: normalizeRiskLabelText(l.label), applicant_type: 'single' as const })),
        ...s.labelsMultiple.map((l) => ({ ...l, label: normalizeRiskLabelText(l.label), applicant_type: 'multiple' as const })),
      ]
      const fbSingle = s.labelsSingle.length > 0 ? s.labelsSingle[0].numeric_value : 1
      const fbMulti = s.labelsMultiple.length > 0 ? s.labelsMultiple[0].numeric_value : 1
      const payload = {
        equifax_ranges_single: s.equifaxSingle.map((r) => ({ id: r.id, min_value: r.min_value, max_value: r.max_value })),
        equifax_ranges_multiple: s.equifaxMultiple.map((r) => ({ id: r.id, min_value: r.min_value, max_value: r.max_value })),
        ninja_ranges_single: s.ninjaSingle.map((r) => ({ id: r.id, min_value: r.min_value, max_value: r.max_value })),
        ninja_ranges_multiple: s.ninjaMultiple.map((r) => ({ id: r.id, min_value: r.min_value, max_value: r.max_value })),
        risk_labels: riskLabelsPayload,
        risk_values: {
          single: buildMatrixPayload('single', s.singleMatrix, s.ninjaSingle, s.equifaxSingle, fbSingle),
          multiple: buildMatrixPayload('multiple', s.multipleMatrix, s.ninjaMultiple, s.equifaxMultiple, fbMulti),
        },
        product_model_single: s.selectedProductModelIds.single,
        product_model_multiple: s.selectedProductModelIds.multiple,
      }

      setSaving(true)
      try {
        await saveRiskMatrix(productId, payload)
        showSuccess('Risk matrix saved successfully!')
        await load()
        return true
      } catch (e) {
        const msg = (e as { message?: string }).message || 'Failed to save risk matrix'
        onError(msg)
        if (msg.includes('already assigned to Applications')) {
          await load()
        }
        return false
      } finally {
        setSaving(false)
      }
    }

    useImperativeHandle(ref, () => ({
      save: saveImpl,
      cancel: () => {
        void load()
      },
      isDirty: () => dirtyRef.current,
      isLocked: () => lockedRef.current,
    }))

    /**
     * Angular `updateRangeValue`: after editing a range, warn immediately if it overlaps a sibling
     * (save still blocks overlaps too). Uses `onError` — same channel as other risk validations.
     */
    const warnIfRangeOverlaps = (ranges: ScoreRange[], index: number, kind: 'Eq' | 'Ninja') => {
      const r = ranges[index]
      if (!r) return
      const lo = Number(r.min_value)
      const hi = Number(r.max_value)
      if (Number.isNaN(lo) || Number.isNaN(hi) || lo > hi) return
      const label = kind === 'Eq' ? 'Eq score' : 'Ninja score'
      const tab = subTab === 'single' ? ' (Single Apps)' : ' (Multiple Apps)'
      for (let j = 0; j < ranges.length; j++) {
        if (j === index) continue
        const o = ranges[j]
        const olo = Number(o.min_value)
        const ohi = Number(o.max_value)
        if (Number.isNaN(olo) || Number.isNaN(ohi) || olo > ohi) continue
        if (scoreRangesOverlap(r, o)) {
          onError(`${label} ranges "${lo}-${hi}" and "${olo}-${ohi}" overlap${tab}. Please fix before saving.`)
          return
        }
      }
    }

    /**
     * Angular `updateRangeValue` binds to (change): free typing in the DOM, commit on blur.
     * Controlled React values + `if (NaN) return` left a leftover digit when the field was cleared
     * (empty → NaN → state never updated → last digit stayed).
     */
    const commitEqRange = (
      index: number,
      field: 'min_value' | 'max_value',
      input: HTMLInputElement,
    ) => {
      const ranges =
        subTab === 'single' ? stateRef.current.equifaxSingle : stateRef.current.equifaxMultiple
      if (!ranges[index]) return
      const num = parseInt(input.value, 10)
      if (Number.isNaN(num)) {
        input.value = String(ranges[index][field])
        return
      }
      if (ranges[index][field] === num) {
        warnIfRangeOverlaps(ranges, index, 'Eq')
        return
      }
      const copy = [...ranges]
      copy[index] = { ...copy[index], [field]: num }
      ;(subTab === 'single' ? setEquifaxSingle : setEquifaxMultiple)(copy)
      setDirty(true)
      warnIfRangeOverlaps(copy, index, 'Eq')
    }

    const commitNjRange = (
      index: number,
      field: 'min_value' | 'max_value',
      input: HTMLInputElement,
    ) => {
      const ranges =
        subTab === 'single' ? stateRef.current.ninjaSingle : stateRef.current.ninjaMultiple
      if (!ranges[index]) return
      const num = parseInt(input.value, 10)
      if (Number.isNaN(num)) {
        input.value = String(ranges[index][field])
        return
      }
      if (ranges[index][field] === num) {
        warnIfRangeOverlaps(ranges, index, 'Ninja')
        return
      }
      const copy = [...ranges]
      copy[index] = { ...copy[index], [field]: num }
      ;(subTab === 'single' ? setNinjaSingle : setNinjaMultiple)(copy)
      setDirty(true)
      warnIfRangeOverlaps(copy, index, 'Ninja')
    }

    const addEq = () => {
      if (locked || saving) return
      const ranges = [...currentEq]
      const last = ranges[ranges.length - 1]
      const newMin = last ? last.max_value + 1 : 0
      const newRange: ScoreRange = { type: 'equifax', min_value: newMin, max_value: newMin + 49 }
      assignTmp(newRange)
      ranges.push(newRange)
      setCurrentEq(ranges)
      const matrix = { ...currentMatrix }
      const defaultVal = currentLabels.length > 0 ? currentLabels[0].numeric_value : 1
      currentNj.forEach((nr, ni) => {
        const nk = getRangeKey(nr, ni)
        const ek = getRangeKey(newRange, ranges.length - 1)
        const key = `${nk}_${ek}`
        if (matrix[key] === undefined) matrix[key] = defaultVal
      })
      setCurrentMatrix(matrix)
      setDirty(true)
    }

    const removeEq = (index: number) => {
      if (locked || saving) return
      const ranges = [...currentEq]
      const range = ranges[index]
      if (!range) return
      const matrix = { ...currentMatrix }
      const ek = getRangeKey(range, index)
      currentNj.forEach((nr, ni) => {
        const nk = getRangeKey(nr, ni)
        delete matrix[`${nk}_${ek}`]
      })
      ranges.splice(index, 1)
      setCurrentEq(ranges)
      setCurrentMatrix(matrix)
      setDirty(true)
    }

    const addNj = () => {
      if (locked || saving) return
      const ranges = [...currentNj]
      const last = ranges[ranges.length - 1]
      const newMin = last ? last.max_value + 1 : 0
      const newRange: ScoreRange = { type: 'ninja', min_value: newMin, max_value: newMin + 9 }
      assignTmp(newRange)
      ranges.push(newRange)
      setCurrentNj(ranges)
      const matrix = { ...currentMatrix }
      const defaultVal = currentLabels.length > 0 ? currentLabels[0].numeric_value : 1
      currentEq.forEach((er, ei) => {
        const ek = getRangeKey(er, ei)
        const nk = getRangeKey(newRange, ranges.length - 1)
        const key = `${nk}_${ek}`
        if (matrix[key] === undefined) matrix[key] = defaultVal
      })
      setCurrentMatrix(matrix)
      setDirty(true)
    }

    const removeNj = (index: number) => {
      if (locked || saving) return
      const ranges = [...currentNj]
      const range = ranges[index]
      if (!range) return
      const matrix = { ...currentMatrix }
      const nk = getRangeKey(range, index)
      currentEq.forEach((er, ei) => {
        const ek = getRangeKey(er, ei)
        delete matrix[`${nk}_${ek}`]
      })
      ranges.splice(index, 1)
      setCurrentNj(ranges)
      setCurrentMatrix(matrix)
      setDirty(true)
    }

    const addLabel = () => {
      if (locked || saving) return
      const labels = [...currentLabels]
      const used = new Set(labels.map((l) => Number(l.numeric_value)))
      let newVal = 1
      while (used.has(newVal)) newVal++
      const colorIdx = labels.length % DEFAULT_RISK_COLORS.length
      labels.push({
        numeric_value: newVal,
        label: '',
        color: DEFAULT_RISK_COLORS[colorIdx],
        access_fee_percentage: 0,
        applicant_type: subTab,
      })
      setCurrentLabels(labels)
      setDirty(true)
    }

    const removeLabel = (index: number) => {
      if (locked || saving) return
      const labels = [...currentLabels]
      if (labels.length <= 1) return
      const removedVal = labels[index]?.numeric_value
      labels.splice(index, 1)
      setCurrentLabels(labels)
      const fallback = labels.length > 0 ? labels[0].numeric_value : 1
      const matrix = { ...currentMatrix }
      if (removedVal != null) {
        Object.keys(matrix).forEach((k) => {
          if (matrix[k] === removedVal) matrix[k] = fallback
        })
      }
      setCurrentMatrix(matrix)
      setDirty(true)
    }

    if (loading) {
      return (
        <div className="flex justify-center py-12 text-slate-500">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      )
    }

    return (
      <div className="min-w-0 space-y-5" data-testid="product-risk-matrix-tab">
        <SectionCard
          icon={ShieldCheck}
          title="Risk Matrix"
          description="Define/update the risk matrix evaluation based on ninja and Eq score ranges. Adjust row labels, column labels, and matrix values as needed."
          testId="product-risk-matrix-section"
          headerActions={
            <div className="flex flex-wrap items-start justify-end gap-3">
              <button
                type="button"
                onClick={() => setLocked((l) => !l)}
                disabled={saving}
                className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                  locked
                    ? 'border-amber-200 bg-amber-50 text-amber-900'
                    : 'border-emerald-200 bg-emerald-50 text-emerald-900'
                }`}
              >
                {locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
                {locked ? 'Locked' : 'Unlocked'}
              </button>
              {currentLabels.length > 0 && (
                <div
                  className="flex min-w-0 flex-col items-end gap-1.5 pt-0.5 sm:min-w-[10rem]"
                  aria-label="Risk level legend"
                >
                  <div className="flex flex-wrap items-center justify-end gap-1.5">
                    {currentLabels.map((lbl) => {
                      const color = lbl.color || 'rgb(211 211 211)'
                      return (
                        <span
                          key={`${lbl.numeric_value}-${lbl.color}`}
                          className="inline-flex h-[22px] w-[26px] items-center justify-center rounded-md border border-black/10 text-xs font-bold"
                          style={{
                            backgroundColor: color,
                            color: getRiskCellTextColor(lbl.color),
                          }}
                        >
                          {lbl.numeric_value}
                        </span>
                      )
                    })}
                  </div>
                  <div className="text-xs font-medium text-slate-500">Risk level</div>
                </div>
              )}
            </div>
          }
        >

        <div className="flex w-full max-w-full border-b border-slate-200 text-sm sm:w-auto">
          <button
            type="button"
            className={`min-w-0 flex-1 border-b-2 px-2 py-2 text-center font-medium transition-colors sm:flex-none sm:px-4 ${
              subTab === 'single'
                ? 'border-[rgb(21_151_255)] text-[rgb(21_151_255)]'
                : 'border-transparent text-slate-600 hover:text-[rgb(21_151_255)]'
            }`}
            onClick={() => setSubTab('single')}
          >
            Single apps
          </button>
          <button
            type="button"
            className={`min-w-0 flex-1 border-b-2 px-2 py-2 text-center font-medium transition-colors sm:flex-none sm:px-4 ${
              subTab === 'multiple'
                ? 'border-[rgb(21_151_255)] text-[rgb(21_151_255)]'
                : 'border-transparent text-slate-600 hover:text-[rgb(21_151_255)]'
            }`}
            onClick={() => setSubTab('multiple')}
          >
            Multiple apps
          </button>
        </div>

        <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
          <h3 className="text-sm font-semibold text-slate-800">
            Risk levels <span className="font-normal text-slate-500">({subTab === 'single' ? 'Single' : 'Multiple'})</span>
          </h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {currentLabels.map((lbl, li) => (
              <div
                key={`${li}-${lbl.numeric_value}-${formVersion}`}
                className="flex min-w-0 max-w-full flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-white p-2 shadow-sm"
              >
                <input
                  type="number"
                  className="no-spinners w-14 rounded border border-slate-200 px-1 py-0.5 text-sm"
                  style={{
                    backgroundColor: hexToLightBg(findRiskLabel(currentLabels, lbl.numeric_value)?.color || '#ccc'),
                  }}
                  defaultValue={lbl.numeric_value}
                  disabled={locked || saving}
                  // Angular `updateRiskLabelNumericValue` binds to (change): commit on blur, not per keystroke.
                  onBlur={(e) => {
                    const input = e.target
                    const newVal = parseInt(input.value, 10)
                    if (Number.isNaN(newVal)) {
                      input.value = String(lbl.numeric_value)
                      return
                    }
                    const oldVal = lbl.numeric_value
                    if (oldVal === newVal) return
                    if (isRiskLevelNumberTaken(currentLabels, newVal, lbl)) {
                      onError('Risk level number must be unique (no duplicates).')
                      input.value = String(oldVal)
                      return
                    }
                    const labels = [...currentLabels]
                    labels[li] = { ...lbl, numeric_value: newVal }
                    setCurrentLabels(labels)
                    const matrix = { ...currentMatrix }
                    Object.keys(matrix).forEach((k) => {
                      if (matrix[k] === oldVal) matrix[k] = newVal
                    })
                    setCurrentMatrix(matrix)
                    setDirty(true)
                  }}
                />
                <input
                  type="color"
                  className="h-8 w-10 cursor-pointer rounded border border-slate-200"
                  value={lbl.color}
                  disabled={locked || saving}
                  onChange={(e) => {
                    const labels = [...currentLabels]
                    labels[li] = { ...lbl, color: e.target.value }
                    setCurrentLabels(labels)
                    setDirty(true)
                  }}
                />
                <input
                  type="text"
                  className="min-w-0 flex-1 rounded border border-slate-200 px-2 py-0.5 text-sm uppercase sm:min-w-[100px] sm:flex-none"
                  value={lbl.label}
                  disabled={locked || saving}
                  onChange={(e) => {
                    const labels = [...currentLabels]
                    labels[li] = { ...lbl, label: formatRiskLabelText(e.target.value, false) }
                    setCurrentLabels(labels)
                    setDirty(true)
                  }}
                  onBlur={(e) => {
                    const labels = [...currentLabels]
                    labels[li] = { ...lbl, label: normalizeRiskLabelText(e.target.value) }
                    setCurrentLabels(labels)
                    setDirty(true)
                  }}
                />
                <input
                  type="number"
                  step="0.01"
                  className="no-spinners w-16 rounded border border-slate-200 px-1 py-0.5 text-sm"
                  defaultValue={lbl.access_fee_percentage}
                  disabled={locked || saving}
                  // Angular `updateRiskLabelPercentage` binds to (change): commit on blur, not per keystroke.
                  onBlur={(e) => {
                    const n = parseFloat(e.target.value)
                    if (Number.isNaN(n)) {
                      e.target.value = String(lbl.access_fee_percentage)
                      return
                    }
                    const labels = [...currentLabels]
                    labels[li] = { ...lbl, access_fee_percentage: n }
                    setCurrentLabels(labels)
                    setDirty(true)
                  }}
                />
                {currentLabels.length > 1 && (
                  <button
                    type="button"
                    disabled={locked || saving}
                    onClick={() => removeLabel(li)}
                    className="rounded p-1 text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              disabled={locked || saving}
              onClick={addLabel}
              className="inline-flex items-center gap-1 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Add risk level
            </button>
          </div>
        </div>

        <div className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-[minmax(0,20%)_minmax(0,20%)_minmax(0,1fr)]">
          <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
            <h4 className="text-sm font-semibold text-slate-800">Eq score range</h4>
            <p className="text-xs text-slate-500">Column labels</p>
            <div className="mt-3 space-y-2">
              {currentEq.map((range, i) => (
                <div
                  key={`${getRangeKey(range, i)}-${formVersion}`}
                  className="group grid grid-cols-[minmax(56px,1fr)_64px_64px_20px] items-center gap-1 border-b border-slate-100 pb-2 text-sm last:border-b-0"
                >
                  <span className="truncate font-medium text-slate-700">{getRangeLabel(range)}</span>
                  <input
                    type="number"
                    className="no-spinners w-16 rounded-md border border-slate-300 bg-slate-50 px-1 py-0.5"
                    disabled={locked || saving}
                    defaultValue={range.min_value}
                    onBlur={(e) => commitEqRange(i, 'min_value', e.target)}
                  />
                  <input
                    type="number"
                    className="no-spinners w-16 rounded-md border border-slate-300 bg-slate-50 px-1 py-0.5"
                    disabled={locked || saving}
                    defaultValue={range.max_value}
                    onBlur={(e) => commitEqRange(i, 'max_value', e.target)}
                  />
                  <button
                    type="button"
                    disabled={locked || saving}
                    onClick={() => removeEq(i)}
                    className="justify-self-end text-rose-600 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                disabled={locked || saving}
                onClick={addEq}
                className="text-sm font-medium text-[rgb(21_151_255)] hover:text-[#0d7acc] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                + Add Eq score range
              </button>
            </div>
          </div>

          <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
            <h4 className="text-sm font-semibold text-slate-800">Ninja score range</h4>
            <p className="text-xs text-slate-500">Row labels</p>
            <div className="mt-3 space-y-2">
              {currentNj.map((range, ni) => (
                <div
                  key={`${getRangeKey(range, ni)}-${formVersion}`}
                  className="group grid grid-cols-[minmax(56px,1fr)_64px_64px_20px] items-center gap-1 border-b border-slate-100 pb-2 text-sm last:border-b-0"
                >
                  <span className="truncate font-medium text-slate-700">{getRangeLabel(range)}</span>
                  <input
                    type="number"
                    className="no-spinners w-16 rounded-md border border-slate-300 bg-slate-50 px-1 py-0.5"
                    disabled={locked || saving}
                    defaultValue={range.min_value}
                    onBlur={(e) => commitNjRange(ni, 'min_value', e.target)}
                  />
                  <input
                    type="number"
                    className="no-spinners w-16 rounded-md border border-slate-300 bg-slate-50 px-1 py-0.5"
                    disabled={locked || saving}
                    defaultValue={range.max_value}
                    onBlur={(e) => commitNjRange(ni, 'max_value', e.target)}
                  />
                  <button
                    type="button"
                    disabled={locked || saving}
                    onClick={() => removeNj(ni)}
                    className="justify-self-end text-rose-600 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                disabled={locked || saving}
                onClick={addNj}
                className="text-sm font-medium text-[rgb(21_151_255)] hover:text-[#0d7acc] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                + Add Ninja score range
              </button>
            </div>
          </div>

          <div className="min-w-0 sm:col-span-2 xl:col-span-1 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
            <h4 className="text-sm font-semibold text-slate-800">Matrix</h4>
            <div className="mt-2 grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-2">
              <div className="hidden items-center justify-center text-xs font-semibold text-slate-500 lg:flex [writing-mode:vertical-rl] [transform:rotate(180deg)]">
                Ninja score range (vertical)
              </div>
              <div className="min-w-0">
                <div className="mb-2 text-center text-xs font-semibold text-slate-500">
                  Eq score range (horizontal)
                </div>
                <div className="max-w-full overflow-x-auto overscroll-x-contain rounded-lg border border-slate-200 bg-slate-50 p-1">
                  <table className="min-w-full border-separate border-spacing-1 text-xs">
                    <thead>
                      <tr>
                        <th className="rounded bg-slate-50 p-1" />
                    {currentEq.map((eq, ei) => (
                          <th
                            key={getRangeKey(eq, ei)}
                            className="min-w-[66px] rounded bg-slate-100 px-1 py-1 font-semibold text-slate-700"
                          >
                        {getRangeLabel(eq)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentNj.map((nj, ni) => (
                        <tr key={getRangeKey(nj, ni)}>
                          <td className="rounded bg-slate-100 px-2 py-1 font-semibold whitespace-nowrap text-slate-700">
                        {getRangeLabel(nj)}
                          </td>
                          {currentEq.map((eq, ei) => {
                            const nk = getRangeKey(nj, ni)
                            const ek = getRangeKey(eq, ei)
                            const val = getMatrixValue(nk, ek)
                            return (
                              <td key={`${nk}-${ek}`} className="rounded p-0.5">
                                <RiskMatrixCellSelect
                                  value={val}
                                  disabled={locked || saving}
                                  options={currentLabels.map((l) => ({
                                    value: l.numeric_value,
                                    color: l.color || '#cbd5e1',
                                  }))}
                                  onChange={(next) => setMatrixValue(nk, ek, next)}
                                />
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="flex items-center gap-1.5 text-sm font-bold text-slate-700">
            Advanced condition rules
            <span className="text-xs font-medium text-slate-500">
              ({subTab === 'single' ? 'Single applicant' : 'Multiple applicant'})
            </span>
          </h3>
          <div className="mt-4 flex max-w-sm items-center gap-3.5">
            <label htmlFor="product_model_id" className="shrink-0 text-xs font-bold text-slate-700">
              Model
            </label>
            <select
              id="product_model_id"
              className="h-[38px] flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 text-xs text-slate-700 outline-none focus:border-[rgb(21_151_255)] focus:ring-1 focus:ring-[rgb(21_151_255)]"
              value={selectedProductModelId ?? ''}
              disabled={locked || saving || productModels.length === 0}
              onChange={(e) => {
                const raw = e.target.value
                const next = raw === '' ? null : Number(raw)
                setSelectedProductModelIds((prev) => ({ ...prev, [subTab]: next }))
                setDirty(true)
              }}
            >
              <option value="">Select model</option>
              {productModels.map((model) => (
                <option key={model.pm_id} value={model.pm_id}>
                  {model.model_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </SectionCard>
    </div>
  )
},
)

export type { ProductRiskMatrixTabHandle } from '../types/components'

